import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { ApolloError } from 'apollo-client';

import {
  useGetMenuCustomizationQuery,
  GqlGetMenuCustomizationQuery,
  NavBarColor,
  LinkColor,
  CustomFont,
  useGetMenuSectionsAdminQuery,
  GqlGetMenuSectionsAdminQuery,
  GqlMenuSection,
  GqlMobileCustomization,
  GqlImageBanner,
  TextTransformOption,
  RadiusOption,
  CategoriesTypeOption,
  CategoriesMobileDisplayOption,
  ProductCardButtonOption,
  GqlCustomizedCategory,
  GqlDispensaries,
  Maybe,
  ProductCardDefaultViewOption,
  ProductCardTextAlignmentOption,
  GqlRemoteExtensions,
  GqlProductCardDisplayOptions,
} from 'types/graphql';

import { removeTypename } from 'shared/helpers/utils';
import useErnie from 'shared/hooks/use-ernie';
import { getCategoriesForMenuForState } from 'shared/helpers/products';
import { useStores } from 'src/hooks/use-stores';
import { EmptyObject, isNotNullish } from 'shared/utils/type-utils';
import { fonts as fontsV1 } from 'shared/constants/menu-fonts';
import { navigationBarColorsV2, linkColorsV2, defaultTagColors } from 'shared/constants/menu-colors';
import { useUI } from 'src/hooks/use-ui';
import { getCategoryPhoto } from 'shared/helpers/dispensaries';
import { MenuBannerColor, isValidMenuBannerColor } from '../helpers';
import { Categories } from '../categories/categories.types';

// Image banner type where schedule timeRange id is optional (not used for banner logic)
type ImageBanner = Omit<GqlImageBanner, 'schedule'> & {
  schedule?:
    | (Omit<NonNullable<GqlImageBanner['schedule']>, 'timeRanges'> & {
        timeRanges?: Array<
          Omit<NonNullable<NonNullable<GqlImageBanner['schedule']>['timeRanges']>[number], 'id'> & { id?: string }
        > | null;
      })
    | null;
};

export type MenuCustomizationData = {
  colorSettings: {
    navBarColor: NavBarColor;
    linkColor: LinkColor;
  };
  fontSettings: {
    family: CustomFont;
  };
  menuBannerColor: MenuBannerColor;
  menuBannerHtml: string | null;
  menuBannerTextBannerCollapsed: boolean;
  ageVerificationBannerColor: MenuBannerColor;
  ageVerificationBannerHtml: string | null;
  disclaimerTextHtml: string | null;
  menuSections: GqlMenuSection[];
  imageBanners: ImageBanner[] | null;
  colors: {
    buttonsLinks: string;
    navBar: string;
    staffPickTag: string | null;
    discountTag: string | null;
    productCardButton: ProductCardButtonOption;
  };
  fonts: {
    body: string;
    heading: string;
  };
  mobile?: GqlMobileCustomization | null;
  textTransforms: {
    buttons: TextTransformOption;
    links: TextTransformOption;
    tags: TextTransformOption;
  };
  radius: {
    default: RadiusOption;
  };
  categoriesV2: {
    type: CategoriesTypeOption;
    style: RadiusOption;
    mobileDisplay: CategoriesMobileDisplayOption;
    items: Categories;
  };
  productCards: {
    defaultViewLarge: ProductCardDefaultViewOption;
    defaultViewSmall: ProductCardDefaultViewOption;
    fullWidthImages: boolean;
    mobileAppDisplayOptions: Omit<GqlProductCardDisplayOptions, '__typename'>;
    showProductWeight: boolean;
    textAlignment: ProductCardTextAlignmentOption;
    disableProductCardImageCycling: boolean;
  };
  remoteExtensions?: Omit<GqlRemoteExtensions, '__typename'> | null;
};

type UseMenuCustomizationDataReturn = {
  data?: MenuCustomizationData;
  error?: ApolloError;
  loading: boolean;
};

type CollectionItem = GqlGetMenuCustomizationQuery['getCollections'][number];

function collectionToCategory(collection: CollectionItem): Categories[number] {
  return {
    category: collection.title,
    image: collection.image ?? '',
    collectionId: collection.id,
    ...(collection.cardImage != null && { cardImage: collection.cardImage }),
    ...(collection.cardDisplayName != null && { cardDisplayName: collection.cardDisplayName }),
    ...(collection.isOutlet != null && { isOutlet: collection.isOutlet }),
  };
}

export function transformCategoriesV2(
  dispensary: EmptyObject | GqlDispensaries,
  sortedCategories: Maybe<GqlCustomizedCategory>[] | null | undefined,
  collections: GqlGetMenuCustomizationQuery['getCollections'] | undefined
): Categories {
  const dispensaryState = dispensary.location?.state;
  const categoriesForState = getCategoriesForMenuForState(dispensaryState);
  const activeCollections = collections ?? [];

  if (sortedCategories) {
    const existingItems: Categories = removeTypename(sortedCategories.filter(isNotNullish));

    // Remove custom categories whose collection no longer exists
    const validCollectionIds = new Set(activeCollections.map((c) => c.id));
    const validItems = existingItems.filter((item) => !item.collectionId || validCollectionIds.has(item.collectionId));

    // Add any standard categories missing from the saved list
    const savedStandardNames = new Set(validItems.filter((item) => !item.collectionId).map((item) => item.category));
    const missingStandard = categoriesForState
      .filter((cat) => !savedStandardNames.has(cat.value))
      .map((category) => ({
        category: category.value,
        image: getCategoryPhoto(dispensary, category),
      }));

    // Add any custom categories (collections) missing from the saved list
    const savedCollectionIds = new Set(validItems.filter((item) => item.collectionId).map((item) => item.collectionId));
    const missingCollections = activeCollections
      .filter((col) => !savedCollectionIds.has(col.id))
      .map(collectionToCategory);

    // Preserve the user's saved ordering; append any newly added categories at the end
    return [...validItems, ...missingStandard, ...missingCollections];
  }

  // No saved settings — build defaults from standard categories + active collections
  const standardCategories = categoriesForState.map((category) => ({
    category: category.value,
    image: getCategoryPhoto(dispensary, category),
  }));

  const customCategories = activeCollections.map(collectionToCategory);

  return [...standardCategories, ...customCategories];
}

function transformData(
  getMenuCustomizationData: GqlGetMenuCustomizationQuery | undefined,
  getMenuSectionsAdminData: GqlGetMenuSectionsAdminQuery | undefined,
  dispensary: EmptyObject | GqlDispensaries
): MenuCustomizationData | undefined {
  if (!getMenuCustomizationData?.filteredDispensaries || !getMenuSectionsAdminData?.getMenuSectionsAdmin) {
    return undefined;
  }
  const item = getMenuCustomizationData.filteredDispensaries[0];

  const { navBarColor, linkColor } = item?.webCustomizationSettings?.colorSettings ?? {};

  const { family } = item?.webCustomizationSettings?.fontSettings ?? {};

  const { buttonsLinks, navBar, staffPickTag, discountTag, productCardButton } =
    item?.webCustomizationSettingsV2?.colors ?? {};
  const fallbackButtonsLinks = linkColor
    ? linkColorsV2[linkColor].background.toLowerCase()
    : linkColorsV2.BLUE_4.background;
  const fallbackNavBar = navBarColor
    ? navigationBarColorsV2[navBarColor].background.toLowerCase()
    : navigationBarColorsV2.ALICE_BLUE.background;
  const { body, heading } = item?.webCustomizationSettingsV2?.fonts ?? {};
  const fallbackFont = family ? fontsV1[family].fontFamily : fontsV1.MATTER.fontFamily;
  const { buttons, links, tags } = item?.webCustomizationSettingsV2?.textTransforms ?? {};
  const { default: defaultRadius } = item?.webCustomizationSettingsV2?.radius ?? {};
  const { categories: customizedCategories } = item?.webCustomizationSettingsV2 ?? {};
  const { remoteExtensions } = item ?? {};
  const { getCollections: collections } = getMenuCustomizationData;
  const colors = {
    buttonsLinks: buttonsLinks ?? fallbackButtonsLinks,
    navBar: navBar ?? fallbackNavBar,
    staffPickTag: staffPickTag ?? defaultTagColors.staffPick,
    discountTag: discountTag ?? defaultTagColors.discount,
    productCardButton: productCardButton ?? ProductCardButtonOption.tinted,
  };

  const fonts = {
    body: body ?? fallbackFont,
    heading: heading ?? fallbackFont,
  };

  const textTransforms = {
    buttons: buttons ?? TextTransformOption.upper,
    links: links ?? TextTransformOption.title,
    tags: tags ?? TextTransformOption.title,
  };

  const radius = {
    default: defaultRadius ?? RadiusOption.rounded,
  };

  const categoriesV2 = {
    type: customizedCategories?.type ?? CategoriesTypeOption.large,
    style: customizedCategories?.style ?? RadiusOption.rounded,
    mobileDisplay: customizedCategories?.mobileDisplay ?? CategoriesMobileDisplayOption.grid,
    items: transformCategoriesV2(dispensary, customizedCategories?.items, collections),
  };

  const legacyLargeDefaultView =
    dispensary.storeSettings?.defaultViewStyle === 'cards'
      ? ProductCardDefaultViewOption.card
      : ProductCardDefaultViewOption.list;

  const productCards = {
    defaultViewLarge: item?.webCustomizationSettingsV2?.productCards?.defaultViewLarge ?? legacyLargeDefaultView,
    defaultViewSmall:
      item?.webCustomizationSettingsV2?.productCards?.defaultViewSmall ?? ProductCardDefaultViewOption.list,
    fullWidthImages: item?.webCustomizationSettingsV2?.productCards?.fullWidthImages ?? false,
    mobileAppDisplayOptions: {
      showCollection: item?.webCustomizationSettingsV2?.productCards?.mobileAppDisplayOptions?.showCollection ?? false,
      showDiscountNames:
        item?.webCustomizationSettingsV2?.productCards?.mobileAppDisplayOptions?.showDiscountNames ?? false,
      showEffects: item?.webCustomizationSettingsV2?.productCards?.mobileAppDisplayOptions?.showEffects ?? false,
      showPotencyDetails:
        item?.webCustomizationSettingsV2?.productCards?.mobileAppDisplayOptions?.showPotencyDetails ?? true,
      showProductWeight:
        item?.webCustomizationSettingsV2?.productCards?.mobileAppDisplayOptions?.showProductWeight ??
        item?.webCustomizationSettingsV2?.productCards?.showProductWeight ??
        true,
      showStaffPick: item?.webCustomizationSettingsV2?.productCards?.mobileAppDisplayOptions?.showStaffPick ?? false,
      showStrainType: item?.webCustomizationSettingsV2?.productCards?.mobileAppDisplayOptions?.showStrainType ?? true,
    },
    showProductWeight: item?.webCustomizationSettingsV2?.productCards?.showProductWeight ?? false,
    textAlignment: item?.webCustomizationSettingsV2?.productCards?.textAlignment ?? ProductCardTextAlignmentOption.left,
    disableProductCardImageCycling:
      item?.webCustomizationSettingsV2?.productCards?.disableProductCardImageCycling ?? false,
  };

  const { menuBannerHtml = null, ageVerificationBannerHtml = null } = item ?? {};

  const { disclaimerTextHtml = null } = item?.embedSettings ?? {};

  const menuBannerColor =
    item?.menuBannerColor && isValidMenuBannerColor(item.menuBannerColor)
      ? item.menuBannerColor
      : MenuBannerColor.green;
  const menuBannerTextBannerCollapsed = item?.menuBannerTextBannerCollapsed ?? true;
  const ageVerificationBannerColor =
    item?.ageVerificationBannerColor && isValidMenuBannerColor(item.ageVerificationBannerColor)
      ? item.ageVerificationBannerColor
      : MenuBannerColor.green;

  const menuSections = getMenuSectionsAdminData.getMenuSectionsAdmin.filter(
    (section: GqlMenuSection | null): section is GqlMenuSection => section !== null
  );

  const imageBanners = item?.imageBanners?.filter(isNotNullish) ?? [];

  return {
    colorSettings: {
      navBarColor: navBarColor ?? NavBarColor.aliceBlue,
      linkColor: linkColor ?? LinkColor.blue_4,
    },
    fontSettings: {
      family: family ?? CustomFont.proximaNova,
    },
    colors,
    fonts,
    mobile: item?.webCustomizationSettingsV2?.mobile,
    textTransforms,
    radius,
    menuBannerHtml,
    menuBannerColor,
    menuBannerTextBannerCollapsed,
    ageVerificationBannerHtml,
    ageVerificationBannerColor,
    disclaimerTextHtml,
    menuSections,
    imageBanners,
    categoriesV2,
    productCards,
    remoteExtensions,
  };
}

export function useGetMenuCustomization(): UseMenuCustomizationDataReturn {
  const { id: dispensaryId } = useParams<{ id: string }>();
  const { apolloClient } = useStores();
  const showErnie = useErnie();
  const UI = useUI();

  const getMenuCustomizationQuery = useGetMenuCustomizationQuery({
    variables: {
      dispensaryId,
    },
    client: apolloClient,
    fetchPolicy: 'no-cache',
  });

  const getMenuSectionsAdminQuery = useGetMenuSectionsAdminQuery({
    variables: {
      dispensaryId,
    },
    client: apolloClient,
    fetchPolicy: 'network-only',
  });

  const error = getMenuCustomizationQuery.error ?? getMenuSectionsAdminQuery.error;
  const loading = getMenuCustomizationQuery.loading || getMenuSectionsAdminQuery.loading;

  useEffect(() => {
    if (error) {
      showErnie('Something went wrong, please try again.', 'danger');
      console.error(error);
    }
  }, [showErnie, error]);

  return {
    data: transformData(getMenuCustomizationQuery.data, getMenuSectionsAdminQuery.data, UI.dispensary),
    loading,
    error,
  };
}
